import React, { useState, useMemo } from 'react';
import { Modal, Select, SelectItem, Checkbox } from 'carbon-components-react';
import useSWR from 'swr';
import { useCustomMutation } from '../../../utils/useCustomMutation';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import { postMethod } from '../../../api/common';
import { formatCurrency } from '../../../utils/common.util';
import { formatAmount } from './financial-aid.page';

const initialFormData = {
  sourceAcademicYearId: '',
  sourceTermId: '',
  targetAcademicYearId: '',
  targetTermId: '',
  copyBillItems: false,
  copyStudents: false,
  programIds: [],
};

const CopyAidProgramsModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState(initialFormData);

  // Fetch academic years
  const { data: academicYears, isLoading: isLoadingAcademicYears } = useSWR(
    'academic-year/list/?skip=0&take=100&query=',
  );

  // Fetch source terms when source academic year is selected
  const sourceTermsKey = formData.sourceAcademicYearId
    ? `academic-year/terms/?academicYearId=${formData.sourceAcademicYearId}`
    : null;
  const { data: sourceTerms } = useSWR(sourceTermsKey);

  // Fetch target terms when target academic year is selected
  const targetTermsKey = formData.targetAcademicYearId
    ? `academic-year/terms/?academicYearId=${formData.targetAcademicYearId}`
    : null;
  const { data: targetTerms } = useSWR(targetTermsKey);

  // Fetch programs for the source academic year and term
  const programsKey =
    formData.sourceAcademicYearId && formData.sourceTermId
      ? `financial-aid/programs?academicYearId=${formData.sourceAcademicYearId}&termId=${formData.sourceTermId}`
      : null;
  const { data: programs, isLoading: isLoadingPrograms } = useSWR(programsKey);

  // Mutation for copying programs
  const copyProgramsMutation = useCustomMutation(
    'financial-aid/programs/rollover',
    (url, { arg }) => postMethod(url, arg),
  );

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
      // Reset term when academic year changes
      ...(field.includes('AcademicYear')
        ? { [field.replace('AcademicYear', 'Term')]: '' }
        : {}),
      // Reset programs when source changes
      ...(field === 'sourceAcademicYearId' || field === 'sourceTermId'
        ? { programIds: [] }
        : {}),
    }));
  };

  const handleClose = () => {
    setFormData(initialFormData);
    onClose?.();
    copyProgramsMutation.reset();
  };

  // Handle individual program checkbox
  const handleProgramCheckbox = (programId, isChecked) => {
    setFormData((prev) => ({
      ...prev,
      programIds: isChecked
        ? [...prev.programIds, programId]
        : prev.programIds.filter((id) => id !== programId),
    }));
  };

  // Handle select all checkbox
  const handleSelectAll = (isChecked) => {
    if (isChecked) {
      const allProgramIds = programs?.map((p) => p.id) || [];
      setFormData((prev) => ({
        ...prev,
        programIds: allProgramIds,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        programIds: [],
      }));
    }
  };

  const isAllSelected = useMemo(() => {
    if (!programs || programs.length === 0) return false;
    return programs.every((p) => formData.programIds.includes(p.id));
  }, [programs, formData.programIds]);

  const isFormValid = useMemo(() => {
    return (
      formData.sourceAcademicYearId &&
      formData.sourceTermId &&
      formData.targetAcademicYearId &&
      formData.targetTermId &&
      formData.programIds.length > 0 &&
      // Prevent copying to the same term if academic year is the same
      !(
        formData.targetAcademicYearId === formData.sourceAcademicYearId &&
        formData.targetTermId === formData.sourceTermId
      ) &&
      // Prevent copying to the same academic year if no term is selected
      !(
        formData.targetAcademicYearId === formData.sourceAcademicYearId &&
        !formData.targetTermId
      )
    );
  }, [formData]);

  const handleSubmit = () => {
    copyProgramsMutation.reset();

    const payload = {
      sourceAcademicYearId: parseInt(formData.sourceAcademicYearId, 10),
      sourceTermId: parseInt(formData.sourceTermId, 10),
      targetAcademicYearId: parseInt(formData.targetAcademicYearId, 10),
      targetTermId: parseInt(formData.targetTermId, 10),
      copyBillItems: formData.copyBillItems,
      copyStudents: formData.copyStudents,
      programIds: formData.programIds.map((id) => parseInt(id, 10)),
    };

    copyProgramsMutation.trigger(payload, {
      onSuccess: () => {
        onSuccess?.();
        handleClose();
      },
    });
  };

  return (
    <Modal
      id="copy-aid-programs-modal"
      className="copy-aid-programs-modal"
      hasScrollingContent
      passiveModal={true}
      open={isOpen}
      modalHeading="Copy Aid Programs"
      primaryButtonDisabled={false}
    >
      <div className="rollover-modal-content">
        <p
          class="font-600 text-primary"
          style={{ fontSize: '25px', padding: '10px 0' }}
        >
          Copy Aid Programs
        </p>
        {/* Note */}
        <div className="rollover-note">
          <p>
            Select the aid programs you want to copy, along with their linked
            bill items and student assignments from the selected source term to
            the target term.
          </p>
        </div>

        {/* Source Section */}
        <div className="rollover-section">
          <h4>Source</h4>
          <div className="rollover-row">
            <div className="rollover-field">
              <Select
                id="sourceAcademicYear"
                labelText="Academic Year"
                value={formData.sourceAcademicYearId}
                onChange={(e) =>
                  handleInputChange('sourceAcademicYearId', e.target.value)
                }
                disabled={isLoadingAcademicYears}
              >
                <SelectItem value="" text="Select Academic Year" />
                {academicYears?.map((year) => (
                  <SelectItem
                    key={year.id}
                    value={year.id}
                    text={year.description}
                  />
                ))}
              </Select>
            </div>
            <div className="rollover-field">
              <Select
                id="sourceTerm"
                labelText="Term"
                value={formData.sourceTermId}
                onChange={(e) =>
                  handleInputChange('sourceTermId', e.target.value)
                }
                disabled={!formData.sourceAcademicYearId}
              >
                <SelectItem value="" text="Select Term" />
                {sourceTerms?.map((term) => (
                  <SelectItem
                    key={term.id}
                    value={term.id}
                    text={term.description}
                  />
                ))}
              </Select>
            </div>
          </div>
        </div>

        {/* Target Section */}
        <div className="rollover-section">
          <h4>Target</h4>
          <div className="rollover-row">
            <div className="rollover-field">
              <Select
                id="targetAcademicYear"
                labelText="Academic Year"
                value={formData.targetAcademicYearId}
                onChange={(e) =>
                  handleInputChange('targetAcademicYearId', e.target.value)
                }
                disabled={isLoadingAcademicYears}
              >
                <SelectItem value="" text="Select Academic Year" />
                {academicYears?.map((year) => (
                  <SelectItem
                    key={year.id}
                    value={year.id}
                    text={year.description}
                  />
                ))}
              </Select>
            </div>
            <div className="rollover-field">
              <Select
                id="targetTerm"
                labelText="Term (Optional)"
                value={formData.targetTermId}
                onChange={(e) =>
                  handleInputChange('targetTermId', e.target.value)
                }
                disabled={!formData.targetAcademicYearId}
              >
                <SelectItem value="" text="Select Term (Optional)" />
                {targetTerms?.map((term) => (
                  <SelectItem
                    key={term.id}
                    value={term.id}
                    text={term.description}
                  />
                ))}
              </Select>
            </div>
          </div>
        </div>

        {/* Programs Selection Section */}
        <div className="rollover-section">
          <h4>Select Programs to Copy</h4>
          {isLoadingPrograms ? (
            <p>Loading programs...</p>
          ) : !programs || programs.length === 0 ? (
            <p>
              No programs found. Please select a source academic year and term.
            </p>
          ) : (
            <div className="programs-table-container">
              <table className="programs-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <Checkbox
                        id="selectAll"
                        checked={isAllSelected}
                        onChange={(value) => handleSelectAll(value)}
                      />
                    </th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Bill Items</th>
                    <th>Assigned Students</th>
                  </tr>
                </thead>
                <tbody>
                  {programs.map((program) => (
                    <tr key={program.id}>
                      <td>
                        <Checkbox
                          id={`program-${program.id}`}
                          checked={formData.programIds.includes(program.id)}
                          onChange={(value) =>
                            handleProgramCheckbox(program.id, value)
                          }
                        />
                      </td>
                      <td>{program.name}</td>
                      <td>{program.type}</td>
                      <td>{formatAmount(program)}</td>
                      <td>{program.billItems?.length || 0}</td>
                      <td>{program.studentCount || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {formData.sourceAcademicYearId &&
            formData.sourceTermId &&
            programs &&
            programs.length > 0 && (
              <p className="selected-count">
                {formData.programIds.length} of {programs.length} programs
                selected
              </p>
            )}
        </div>

        {/* Options Section */}
        <div className="rollover-section">
          <h4>Options</h4>
          <div className="rollover-checkboxes">
            <Checkbox
              id="copyBillItems"
              labelText="Copy Bill Items"
              checked={formData.copyBillItems}
              onChange={(value) => {
                handleInputChange('copyBillItems', value);
              }}
            />
            <Checkbox
              id="copyStudents"
              labelText="Copy Students"
              checked={formData.copyStudents}
              onChange={(value) => {
                handleInputChange('copyStudents', value);
              }}
            />
          </div>
        </div>

        {/* Error Message */}
        {copyProgramsMutation.error && (
          <div className="rollover-error">
            <p>{copyProgramsMutation.error?.message}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="rollover-actions">
          <CustomButton
            classes="outline no-hover font-bold danger"
            style={{ flex: 1 }}
            onClick={handleClose}
          >
            Cancel
          </CustomButton>
          <CustomButton
            style={{ flex: 1 }}
            classes="font-bold primary"
            disabled={!isFormValid}
            onClick={handleSubmit}
            isLoading={copyProgramsMutation.isMutating}
          >
            Copy Programs
          </CustomButton>
        </div>
      </div>
    </Modal>
  );
};

export default CopyAidProgramsModal;
